import styled from '@emotion/styled';
import { css } from '@emotion/react';
import Button from '@base/Button/Button';
import PropTypes from 'prop-types';
import { Spacings, Fonts } from '@styles/index';
import { TEST_CREATION_FLOW_TYPE } from '@constants/quiz';
import {
  TEST_CONTENT_STATE,
  QUIZ_CONTENT_STATE,
  quizSecondaryType
} from '@constants/index';
import P2 from '@base/P2/P2';
import Image from '@base/Image';

const StyledButton = styled(Button)`
  font-size: ${Fonts.SMALL};
  margin-left: auto;
  margin-right: ${(props) =>
    !props.$showNoQuestionsBoxMessage ? Spacings.SPACING_9B : ''};

  &:hover {
    box-shadow: none;
  }
`;

const StyledP2 = styled(P2)`
  margin-left: ${Spacings.SPACING_2B};
`;

const ButtonWrapper = styled.div`
  button {
    margin-right: 0;
  }
  ${(props) =>
    props.$showNoQuestionsBoxMessage &&
    css`
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      padding-top: ${Spacings.SPACING_2B};
      border-top: 1px solid var(--color-i-divider);
    `}
`;

const LABELS = {
  REVIEW: 'Review',
  ADD_QUESTION: 'Add question',
  CREATE_TEST: 'Create test',
  EDIT_QUESTIONS: 'Edit questions',
  CREATE_QUIZ: 'Create quiz'
};

const getTestStateInfo = (contentState, creationType, sessionUID) => {
  switch (contentState) {
    case TEST_CONTENT_STATE.TEMPLATE_PENDING:
      return {
        label: LABELS.CREATE_TEST,
        url: `/educator/test/${sessionUID}?creationType=test`
      };

    case TEST_CONTENT_STATE.ADD_QUESTION:
      return {
        label: LABELS.ADD_QUESTION,
        url: `/educator/test/${sessionUID}`
      };

    case TEST_CONTENT_STATE.EDIT_QUESTION:
    case TEST_CONTENT_STATE.QUESTION_COMPLETED:
      return {
        label: LABELS.EDIT_QUESTIONS,
        url:
          creationType === TEST_CREATION_FLOW_TYPE.AUTOFILLED_CREATION
            ? `/educator/test/autofill/${sessionUID}`
            : `/educator/test/edit/${sessionUID}`
      };

    default:
      return {};
  }
};

const getQuizStateInfo = (contentState, sessionUID) => {
  switch (contentState) {
    case QUIZ_CONTENT_STATE.CREATE_QUIZ:
      return {
        label: LABELS.CREATE_QUIZ,
        url: `/educator/quiz/details/${sessionUID}`
      };

    case QUIZ_CONTENT_STATE.ADD_QUESTION:
    case QUIZ_CONTENT_STATE.EDIT_QUESTION:
      return {
        label: LABELS.ADD_QUESTION,
        url: `/educator/quiz/edit/${sessionUID}`
      };

    case QUIZ_CONTENT_STATE.QUESTION_COMPLETED:
      return {
        label: LABELS.REVIEW,
        url: `/educator/quiz/review/${sessionUID}/?exitFrom=top`
      };

    default:
      return {};
  }
};

const openURL = (url) => window.open(url, '_blank');

const handleStateClick =
  (URL = '', sourceURL) =>
  () => {
    if (URL.includes('?') && sourceURL) {
      openURL(`${URL}&source=${sourceURL}`);

      return;
    }

    if (sourceURL) {
      openURL(`${URL}/?source=${sourceURL}`);

      return;
    }

    openURL(URL);
  };

const StatusButton = ({
  sourceURL,
  isTestSeries,
  contentState,
  creationType,
  sessionUID,
  quizType,
  showNoQuestionsBoxMessage
}) => {
  let statusInfo = isTestSeries
    ? getTestStateInfo(contentState, creationType, sessionUID)
    : getQuizStateInfo(contentState, sessionUID);

  if (quizType === quizSecondaryType.TEST && !isTestSeries)
    statusInfo = {
      label: 'View questions',
      url: `/educator/test/review/${sessionUID}?source=${sourceURL}`
    };

  return statusInfo?.url ? (
    <ButtonWrapper $showNoQuestionsBoxMessage={showNoQuestionsBoxMessage}>
      {showNoQuestionsBoxMessage && statusInfo.label !== LABELS.REVIEW && (
        <>
          <Image src="info-icon.svg" alt="infoIcon" width="24" height="24" />
          <StyledP2>No questions added </StyledP2>
        </>
      )}
      <StyledButton
        $showNoQuestionsBoxMessage={showNoQuestionsBoxMessage}
        label={statusInfo.label}
        color="var(--color-text-primary)"
        maxHeight="sm"
        grid={3}
        onClick={handleStateClick(statusInfo.url, sourceURL)}
      />
    </ButtonWrapper>
  ) : null;
};

export default StatusButton;

StatusButton.propTypes = {
  sourceURL: PropTypes.string,
  isTestSeries: PropTypes.bool.isRequired,
  contentState: PropTypes.number.isRequired,
  creationType: PropTypes.number,
  sessionUID: PropTypes.string.isRequired,
  quizType: PropTypes.number
};

StatusButton.defaultProps = {
  sourceURL: null,
  quizType: 0,
  creationType: null
};
